import { hasFullAccess } from '../../../../../../common/common-types/user-scopes';
import { environment } from '../../environments/enviroment';
import { computed, DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { IGlobalUser, IUserOrganizationData } from '@common-interfaces/global-user';
import { IClientOrganization } from '@common-interfaces/client-organization';
import { Api } from './api.service';
import { debugLogger } from '@common-helpers/debug-logger';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private api = inject(Api);
    private document = inject(DOCUMENT);
    /** Internal source of truth. */
    private user = signal<IGlobalUser | null>(null);

    /** Publicly readable copy of the current user. */
    currentUser = signal<IGlobalUser | null>(null);
    currentOrganization = signal<IUserOrganizationData | null>(null);
    organization = signal<IClientOrganization | null>(null);
    clientsInOrganization = signal<any[]>([]);

    /** Venues in the current organisation this user may open: all of them with full access, otherwise their venue access list. */
    accessibleVenues = computed<{ _id: string; name: string; settings?: any }[]>(() => {
        const venues = this.clientsInOrganization() ?? [];
        const user = this.currentUser();
        if (hasFullAccess(user, this.organizationId())) {
            return venues;
        }
        const allowed = new Set((user?.associatedClients ?? []).map(String));
        return venues.filter((venue) => allowed.has(String(venue._id)));
    });

    /** Sends the browser to the client app's sign-in, where every Funbutler user logs in. */
    redirectToLogin(): void {
        this.document.location.href = `${environment.clientAppBaseUrl ?? '/client'}/login`;
    }

    /** A page in the client app (the venue's own admin) for one venue, e.g. its calendar. */
    clientAppUrl(clientId: string, path = ''): string {
        return `${environment.clientAppBaseUrl}/${clientId}${path ? '/' + path : ''}`;
    }
    organizationId = signal<string>('');

    availableOrganizations = signal<{ _id: string; name: string }[]>([]);

    /** In-flight request, used to de-dupe concurrent callers. */
    private userFetchPromise: Promise<IGlobalUser | null> | null = null;

    constructor() {
        this.get();
    }

    /**
     * Sets the current organization the user is accessing.
     */
    async setOrganization(orgId: string): Promise<void> {
        // A second caller for the same organisation waits for the first load instead of seeing it half done.
        if (this.organizationLoad?.orgId === orgId) {
            return this.organizationLoad.promise;
        }
        const changed = this.organizationId() !== orgId;
        this.organizationId.set(orgId);
        this.api.organizationId.set(orgId);
        const promise = (async () => {
            await this.getFullOrganization(orgId);
            if (changed) {
                await this.getAssociatedClients();
            }
            debugLogger.info('this.organization()', this.organization());
        })();
        this.organizationLoad = { orgId, promise };
        try {
            await promise;
        } finally {
            if (this.organizationLoad?.promise === promise) {
                this.organizationLoad = null;
            }
        }
    }

    private organizationLoad: { orgId: string; promise: Promise<void> } | null = null;

    async getAssociatedClients() {
        const orgId = this.organizationId();
        let clients = await this.api.currentorganization().get('/associated-clients');

        // A slower response for an org we've since switched away from must not overwrite the current list.
        if (clients.success && orgId === this.organizationId()) {
            this.clientsInOrganization.set(clients.data as []);
            debugLogger.info('clients', this.clientsInOrganization());
        }
    }

    isUser(username: string): boolean {
        const u = this.user();
        return !!u && u.username === username;
    }

    async get(): Promise<IGlobalUser | null> {
        // Already cached.
        if (this.currentUser()) {
            return this.currentUser();
        }

        // A request is already running — reuse it instead of firing another.
        if (this.userFetchPromise) {
            return this.userFetchPromise;
        }

        this.userFetchPromise = this.fetchCurrentUser();

        try {
            return await this.userFetchPromise;
        } finally {
            this.userFetchPromise = null;
        }
    }

    async getOrganizationAccess(): Promise<void> {
        let user = this.currentUser();
        if (user && user.organizationData && user.role !== 'superadmin') {
            let orgs = Object.keys(user.organizationData);

            let orgsForSelection = [];

            for (let org of orgs) {
                if (user.organizationData[org].organizationName) {
                    orgsForSelection.push({
                        name: user.organizationData[org].organizationName,
                        _id: org
                    });
                }
            }
            this.availableOrganizations.set(orgsForSelection);
        } else {
            let orgs = await this.api.superAdmin().get<{ _id: string; name: string }[]>('/organizations/list');
            let orgData = orgs.data;

            if (!orgData) {
                return;
            }
            this.availableOrganizations.set(orgData);
            if (orgs.data) {
                let newOrgData: { [organizationId: string]: IUserOrganizationData } = {};

                for (let org of orgs.data) {
                    newOrgData[org._id] = {
                        organizationName: org.name,
                        scopes: ['super'],
                        roleId: null
                    };
                }
                this.currentUser.set({
                    ...this.currentUser(),
                    organizationData: newOrgData
                });
            }
        }
    }

    private async getFullOrganization(id: string): Promise<void> {
        try {
            const res = await this.api.organization().get<IClientOrganization>(`/${id}`);

            // A slower response for an org we've since switched away from must not overwrite the current one.
            if (res.success && id === this.organizationId()) {
                this.organization.set(res.data);
            } else {
            }
        } catch (e) {
            debugLogger.error('e', e);
        }
    }

    private async fetchCurrentUser(): Promise<IGlobalUser | null> {
        try {
            const res = await this.api.admin().get<IGlobalUser>('/users/current');

            if (!res.success || !res.data) {
                return null;
            }

            this.user.set(res.data);
            debugLogger.info('this.user()', this.user());
            // The organisation is picked by OrgGuard (from the URL) or ValidateComponent (the first one), not here:
            // an unawaited pick here raced the deep-linked organisation.
            this.currentUser.set(this.user());

            // Superadmins get organizationData from here, and OrgGuard needs it before it can let a deep link through.
            await this.getOrganizationAccess();

            return this.currentUser();
        } catch (error) {
            const status = (error as { status?: number })?.status;

            if (status !== 401) {
                debugLogger.info('error', error);
            }
            // Not signed in (401): callers send the user to the client app's login.
            return null;
        }
    }
}
